import * as React from 'react';
import { observer } from 'mobx-react';
import { IDynamicRedirection } from './IDynamicRedirection';
import styled from 'styled-components';
import { Breakpoints } from '@estee/elc-base-theme';
import { classes } from '~utils/Classes';
import LoyaltyMarketingPageController from '~exported/loyalty-marketing-page/LoyaltyMarketingPageController';
import { vulcan_csrf_token } from '~api/LoyaltyApiSdk';

export const TEST_ID_DYNAMIC_REDIRECTION_SECTION = 'loyalty-marketing-page-dynamic-redirection';

const DynamicRedirectionBannerClass = classes('dynamic-redirection-program-banner');
const DynamicRedirectionClass = classes(
    'loyalty-marketing-page-dynamic-redirection-section-wrapper'
);
const DynamicRedirectionClassBannerParent = classes(
    'loyalty-marketing-page-dynamic-redirection-banner-parent'
);
const DynamicRedirectionFormClass = classes('dynamic-redirection-form');
const DynamicRedirectionHeaderClass = classes('dynamic-redirection-header');
const DynamicRedirectionImageClass = classes('dynamic-redirection-with-image');
const DynamicRedirectionImageLoyaltyClass = classes('dynamic-redirection-with-image--loyalty');
const DynamicRedirectionJoinWrapperClass = classes('dynamic-redirection-join-wrapper');
const DynamicRedirectionJoinButtonClass = classes('dynamic-redirection-join-btn');
const DynamicRedirectionMemberClass = classes('dynamic-redirection-member');
const DynamicRedirectionProgramNameClass = classes('dynamic-redirection-program-name');
const DynamicRedirectionSubheaderClass = classes('dynamic-redirection-subheader');
const DynamicRedirectionDescriptionClass = classes('dynamic-redirection-description');
const DynamicRedirectionWrapperClass = classes('loyalty-marketing-page-dynamic-redirection');
const DynamicRedirectionHeaderWrapperClass = classes('dynamic-redirection-header-wrapper');

const layout = `
'redirectionSection bannerImage'`;

const DynamicRedirectionWrapper = styled.div<{
    componentType?: string;
    bannerExists?: boolean;
    sectionBackgroundColor?: string;
    sectionTextColor?: string;
}>`
    grid-area: ${({ componentType }) => `${componentType}`};
    background-color: ${({ sectionBackgroundColor }) => `#${sectionBackgroundColor}`};
    color: ${({ sectionTextColor }) => `#${sectionTextColor}`};
    ${({ bannerExists }) =>
        bannerExists
            ? `
    @media screen and (min-width: ${Breakpoints.desktop}px) {
        grid-template-areas: ${layout};
    }
    `
            : ''}
`;

const DynamicRedirectionBannerParent = styled.div`
    grid-area: dynamicRedirectionBanner;
`;
const DynamicRedirectionBannerImage = styled.div`
    grid-area: bannerImage;
`;
const DynamicRedirectionEmailField = styled.input`
    grid-area: DynamicRedirectionEmailField;
`;
const DynamicRedirectionForm = styled.form`
    grid-area: DynamicRedirectionForm;
`;
const DynamicRedirectionHeader = styled.div`
    grid-area: DynamicRedirectionHeader;
`;
const DynamicRedirectionJoinWrapper = styled.div`
    grid-area: DynamicRedirectionJoinWrapper;
`;

const DynamicRedirectionJoinButton = styled.input`
    grid-area: DynamicRedirectionJoinButton;
`;

const DynamicRedirectionMember = styled.div`
    grid-area: DynamicRedirectionMember;
`;
const DynamicRedirectionProgramName = styled.div`
    grid-area: DynamicRedirectionProgramName;
`;
const DynamicRedirectionSectionWrapper = styled.div<{ bannerExists?: boolean }>`
    grid-area: redirectionSection;
`;
const DynamicRedirectionSubheader = styled.div`
    grid-area: DynamicRedirectionSubheader;
`;
const DynamicRedirectionDescription = styled.div`
    grid-area: DynamicRedirectionDescription;
`;
const DynamicRedirectionHeaderWrapper = styled.div``;

export interface IDynamicRedirectionProps {
    viewController: LoyaltyMarketingPageController;
    dynamicRedirection: IDynamicRedirection;
    type: string;
}

export const DynamicRedirection = observer(function DynamicRedirectionSection({
    dynamicRedirection: {
        description,
        emailPlaceholder,
        guestButton,
        loyaltyBannerImage,
        loyaltyButton,
        loyaltyHeader,
        loyaltyMemberSignIn,
        loyaltyProgramName,
        nonLoyaltyHeader,
        onlineButton,
        sectionBackgroundColor,
        sectionButtonColors,
        sectionTextColor,
        subheader
    },
    viewController: {
        data: { isLoyaltyUser, isOnlineUser },
        onLinkClick
    },
    type
}: IDynamicRedirectionProps) {
    const buttonState = isLoyaltyUser ? loyaltyButton : isOnlineUser ? onlineButton : guestButton;
    const headerState = isLoyaltyUser ? loyaltyHeader : nonLoyaltyHeader;
    const isGuestUser = !isOnlineUser && !isLoyaltyUser;
    const isNonLoyaltyUser = !isLoyaltyUser;
    const displaySubheader = !!subheader?.length;
    const displayDescription = !!description?.length;
    const bannerExists = !!loyaltyBannerImage?.src?.length;
    const handleLinkClick = (e: React.MouseEvent<HTMLInputElement>) => {
        const target = e.target as HTMLAnchorElement;
        if (target.tagName === 'A') {
            onLinkClick(`${target.innerText}`);
        }
    };

    return bannerExists ? (
        <DynamicRedirectionBannerParent className={DynamicRedirectionClassBannerParent}>
            <DynamicRedirectionWrapper
                bannerExists
                className={`${DynamicRedirectionWrapperClass} ${DynamicRedirectionImageClass} ${
                    isLoyaltyUser ? DynamicRedirectionImageLoyaltyClass : ''
                }`}
                sectionBackgroundColor={sectionBackgroundColor.hex}
                sectionTextColor={sectionTextColor.hex}
            >
                <DynamicRedirectionSectionWrapper
                    bannerExists={bannerExists}
                    className={DynamicRedirectionClass}
                    data-test-id={TEST_ID_DYNAMIC_REDIRECTION_SECTION}
                >
                    <DynamicRedirectionProgramName className={DynamicRedirectionProgramNameClass}>
                        {loyaltyProgramName}
                    </DynamicRedirectionProgramName>
                    <DynamicRedirectionHeaderWrapper className={DynamicRedirectionHeaderWrapperClass}>
                        <DynamicRedirectionHeader
                            className={DynamicRedirectionHeaderClass}
                            dangerouslySetInnerHTML={{ __html: headerState }}
                        />
                        {displaySubheader && (
                            <DynamicRedirectionSubheader
                                className={DynamicRedirectionSubheaderClass}
                                dangerouslySetInnerHTML={{ __html: subheader }}
                            />
                        )}
                    </DynamicRedirectionHeaderWrapper>
                    <DynamicRedirectionForm
                        method="POST"
                        action={buttonState.url}
                        className={DynamicRedirectionFormClass}
                    >
                        {isGuestUser && (
                            <DynamicRedirectionEmailField
                                type="email"
                                placeholder={emailPlaceholder}
                                name="LOYALTY_EMAIL_ADDRESS"
                            ></DynamicRedirectionEmailField>
                        )}
                        <input type="hidden" name="LOYALTY_ENROLLMENT" value={type} />
                        {vulcan_csrf_token && (<input type="hidden" name="_csrf_token" value={vulcan_csrf_token} />)}
                        {isNonLoyaltyUser && displayDescription && (
                            <DynamicRedirectionDescription
                                className={DynamicRedirectionDescriptionClass}
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        )}
                        <DynamicRedirectionJoinWrapper
                            className={DynamicRedirectionJoinWrapperClass}
                        >
                            <DynamicRedirectionJoinButton
                                className={DynamicRedirectionJoinButtonClass}
                                title={buttonState.label}
                                type="submit"
                                style={{backgroundColor: `#${sectionButtonColors.backgroundColor}`, color: `#${sectionButtonColors.color}`}}
                                value={buttonState.label}
                                onClick={() => onLinkClick(`${buttonState.label}`)}
                            />
                        </DynamicRedirectionJoinWrapper>
                    </DynamicRedirectionForm>
                    {isGuestUser && (
                        <DynamicRedirectionMember
                            className={DynamicRedirectionMemberClass}
                            dangerouslySetInnerHTML={{ __html: loyaltyMemberSignIn }}
                            onClick={handleLinkClick}
                            role="none"
                        />
                    )}
                </DynamicRedirectionSectionWrapper>
                <DynamicRedirectionBannerImage className={DynamicRedirectionBannerClass}>
                    <div className='banner-image' />
                </DynamicRedirectionBannerImage>
            </DynamicRedirectionWrapper>
        </DynamicRedirectionBannerParent>
    ) : (
        <DynamicRedirectionWrapper
            componentType={type}
            className={DynamicRedirectionWrapperClass}
            sectionBackgroundColor={sectionBackgroundColor.hex}
            sectionTextColor={sectionTextColor.hex}
        >
            <DynamicRedirectionSectionWrapper
                bannerExists={bannerExists as boolean}
                className={DynamicRedirectionClass}
                data-test-id={TEST_ID_DYNAMIC_REDIRECTION_SECTION}
            >
                <DynamicRedirectionProgramName className={DynamicRedirectionProgramNameClass}>
                    {loyaltyProgramName}
                </DynamicRedirectionProgramName>
                <DynamicRedirectionHeader
                    className={DynamicRedirectionHeaderClass}
                    dangerouslySetInnerHTML={{ __html: headerState }}
                />
                {displaySubheader && (
                    <DynamicRedirectionSubheader
                        className={DynamicRedirectionSubheaderClass}
                        dangerouslySetInnerHTML={{ __html: subheader }}
                    />
                )}
                <DynamicRedirectionForm
                    method="POST"
                    action={buttonState.url}
                    className={DynamicRedirectionFormClass}
                >
                    {isGuestUser && (
                        <DynamicRedirectionEmailField
                            type="email"
                            placeholder={emailPlaceholder}
                            name="LOYALTY_EMAIL_ADDRESS"
                        ></DynamicRedirectionEmailField>
                    )}
                    <input type="hidden" name="LOYALTY_ENROLLMENT" value={type} />
                    {vulcan_csrf_token && (<input type="hidden" name="_csrf_token" value={vulcan_csrf_token} />)}
                    {isNonLoyaltyUser && displayDescription && (
                        <DynamicRedirectionDescription
                            className={DynamicRedirectionDescriptionClass}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    )}
                    <DynamicRedirectionJoinWrapper className={DynamicRedirectionJoinWrapperClass}>
                        <DynamicRedirectionJoinButton
                            className={DynamicRedirectionJoinButtonClass}
                            title={buttonState.label}
                            style={{backgroundColor: `#${sectionButtonColors.backgroundColor}`, color: `#${sectionButtonColors.color}`}}
                            type="submit"
                            value={buttonState.label}
                            onClick={() => onLinkClick(`${buttonState.label}`)}
                        />
                    </DynamicRedirectionJoinWrapper>
                </DynamicRedirectionForm>
                {isGuestUser && (
                    <DynamicRedirectionMember
                        className={DynamicRedirectionMemberClass}
                        dangerouslySetInnerHTML={{ __html: loyaltyMemberSignIn }}
                        onClick={handleLinkClick}
                        role="none"
                    />
                )}
            </DynamicRedirectionSectionWrapper>
        </DynamicRedirectionWrapper>
    );
});
